/**
 * PixelPlaceholder — deterministic pixel-art tile used when an item has no media.
 * Seeded by a string (title/slug) so the same item always renders the same pattern.
 * Feels intentional (part of ITZKATU identity) rather than a gray box.
 */
import { useMemo } from "react";

// Cheap deterministic hash → int
function hash(str) {
    let h = 2166136261;
    for (let i = 0; i < (str || "itzkatu").length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

const PALETTES = [
    ["#0E0E0E", "#5D3FD3", "#F0C56C"], // black + purple + gold
    ["#111111", "#00E0FF", "#5D3FD3"], // black + cyan + purple
    ["#0B0B0C", "#F0C56C", "#FFFFFF"], // charcoal + gold
    ["#111111", "#FFFFFF", "#5D3FD3"], // black + white + purple
    ["#1a0f1f", "#F0C56C", "#5D3FD3"], // deep-plum + gold
];

export default function PixelPlaceholder({ seed = "itzkatu", size = 12, className = "" }) {
    // useMemo keeps the grid stable per seed so hovers don't reshuffle
    const { cells, palette } = useMemo(() => {
        const h = hash(seed);
        const palette = PALETTES[h % PALETTES.length];
        const half = Math.ceil(size / 2);
        // Generate left half then mirror horizontally for a symmetric "glyph" feel
        const cells = [];
        let x = h;
        for (let row = 0; row < size; row++) {
            const rowCells = [];
            for (let col = 0; col < half; col++) {
                x = (x * 1664525 + 1013904223) >>> 0;
                const p = x % 100;
                const val =
                    p < 55 ? 0 : // empty / bg
                    p < 85 ? 1 :
                    p < 97 ? 2 : 3;
                rowCells.push(val);
            }
            const mirrored = rowCells.slice().reverse();
            const full = size % 2 === 0
                ? [...rowCells, ...mirrored]
                : [...rowCells, ...mirrored.slice(1)];
            cells.push(full.slice(0, size));
        }
        return { cells, palette };
    }, [seed, size]);

    return (
        <div
            className={`relative w-full h-full overflow-hidden ${className}`}
            style={{ backgroundColor: palette[0] }}
            aria-hidden="true"
        >
            <div
                className="absolute inset-[8%] grid"
                style={{
                    gridTemplateColumns: `repeat(${size}, 1fr)`,
                    gridTemplateRows: `repeat(${size}, 1fr)`,
                    gap: "2px",
                }}
            >
                {cells.flatMap((row, r) =>
                    row.map((v, c) => (
                        <div
                            key={`${r}-${c}`}
                            style={{
                                backgroundColor:
                                    v === 0 ? "transparent" :
                                    v === 1 ? palette[1] :
                                    v === 2 ? palette[2] :
                                    palette[1],
                                opacity: v === 3 ? 0.35 : 1,
                            }}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
